import React, { useEffect, useState } from "react";
import { Download, Share, Smartphone, X } from "lucide-react";

/**
 * PWA install prompt for the admin.
 *
 * Desktop/Chrome on Android: browser fires `beforeinstallprompt`; we show a
 * single "Install as app" button that triggers the native install dialog.
 *
 * iOS Safari: no programmatic install API — we surface a short "Add to Home
 * Screen" instruction card instead (Share button → Add to Home Screen).
 *
 * Hidden once the app is already running in standalone mode, or the user
 * dismisses the banner this session.
 */
export default function InstallAppButton() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(
    typeof sessionStorage !== "undefined" && sessionStorage.getItem("sac-install-dismissed") === "1"
  );
  const [isIOS, setIsIOS] = useState(false);
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    setIsIOS(/iPad|iPhone|iPod/.test(ua) && !/CriOS|FxiOS/.test(ua));
    const inStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setStandalone(inStandalone);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (standalone || dismissed) return null;
  if (!deferred && !isIOS) return null;

  const dismiss = () => {
    sessionStorage.setItem("sac-install-dismissed", "1");
    setDismissed(true);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setDeferred(null);
    }
  };

  return (
    <div
      className="admin-surface p-4 mt-6 border-l-4 border-[#8EC9C7] relative"
      data-testid="install-app-banner"
    >
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 p-1 text-[#8A7C92] hover:text-[#5D2C8F] rounded"
        aria-label="Dismiss"
        data-testid="install-dismiss"
      >
        <X size={14} />
      </button>
      <div className="flex items-start gap-3 pr-8">
        <Smartphone size={20} className="text-[#5D2C8F] mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-display text-lg text-[#5D2C8F]">Install this admin as an app</div>
          {deferred ? (
            <>
              <p className="text-sm text-[#6E6074] mt-1 leading-relaxed">
                Get a dedicated icon on your home screen — opens fullscreen (no browser bars),
                works the same as it does here, and one tap puts you straight into the CRM.
              </p>
              <button
                onClick={install}
                className="sac-btn-primary !py-2 !px-4 text-sm mt-3"
                data-testid="install-app-btn"
              >
                <Download size={14} /> Install app
              </button>
            </>
          ) : (
            /* iOS instructions — Safari has no programmatic install */
            <>
              <p className="text-sm text-[#6E6074] mt-1 leading-relaxed">
                On your iPhone or iPad, tap the <Share size={14} className="inline mx-1 align-text-bottom" />
                <strong>Share</strong> button in Safari, then scroll down and tap <strong>"Add to Home Screen"</strong>.
                The admin will appear on your home screen like a real app.
              </p>
              <p className="text-xs text-[#8A7C92] mt-2 italic">
                Note: this only works in Safari, not Chrome on iOS.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
